import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import iconUser from "../../assets/images/User_icon_2.svg.png";
import { MdDeleteForever } from "react-icons/md";
import { useNotification } from "../../Notification/Notification";
import ComLoading from "../../Components/ComLoading/ComLoading";
import { HiOutlineSearch } from "react-icons/hi";

const ChatApp = () => {
  const navigate = useNavigate();
  const [userLogin, setUserLogin] = useState(null); // State lưu thông tin người dùng đã đăng nhập
  const [selectedUser, setSelectedUser] = useState(null); // State lưu người dùng được chọn để trò chuyện
  const [listUsers, setListUsers] = useState([]); // State lưu danh sách người dùng
  const [conversation, setConversation] = useState(null); // State lưu cuộc trò chuyện hiện tại
  const [messages, setMessages] = useState([]); // State lưu tin nhắn trong cuộc trò chuyện
  const [inputMessage, setInputMessage] = useState(""); // State lưu nội dung tin nhắn đang nhập
  const [, setError] = useState(""); // State lưu thông báo lỗi (không sử dụng)
  const [newMessageNoti, setNewMessageNoti] = useState([]); // State lưu thông báo tin nhắn mới
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState("Practitioner");

  const [isLoadingUsers, setLoadingUsers] = useState(true); // State kiểm tra trạng thái đang tải danh sách người dùng
  const [isLoadingConversation, setLoadingConversation] = useState(false); // State kiểm tra trạng thái đang tải lịch sử trò chuyện
  const chatContainerRef = useRef(null); // Ref để thao tác với phần容器 chứa tin nhắn
  const connectionRef = useRef(null); // Ref lưu kết nối SignalR
  const conversationRef = useRef(); // Ref lưu cuộc trò chuyện hiện tại
  const { notificationApi } = useNotification(); // API thông báo
  const [lastReadMessageId, setLastReadMessageId] = useState(null); // State lưu ID tin nhắn đã đọc cuối cùng

  // Xử lý khi người dùng chọn một người để trò chuyện
  const handleSelectUser = (user) => {
    setSelectedUser(user); // Cập nhật người dùng được chọn
    setMessages([]); // Xóa tin nhắn hiện tại
    conversationRef.current = null; // Xóa cuộc trò chuyện hiện tại
    setLoadingConversation(true); // Bắt đầu tải lịch sử trò chuyện

    // Gọi hàm LoadChatHistory trên server qua SignalR
    connectionRef.current
      ?.invoke("LoadChatHistory", userLogin?.account.id, user.id)
      .catch((err) => {
        console.error("Error loading chat history:", err);
        setLoadingConversation(false);
      });
  };

  // Xử lý khi gửi tin nhắn
  const handleSendMessage = () => {
    if (!inputMessage.trim()) {
      // Kiểm tra nội dung tin nhắn không rỗng
      setError("Message cannot be empty.");
      return;
    }

    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      // Kiểm tra kết nối SignalR
      const message = {
        conversationId: conversation?.id,
        senderId: userLogin?.account.id,
        receiverId: selectedUser?.id,
        content: inputMessage,
      };

      // Gọi hàm SendMessage trên server qua SignalR
      connectionRef.current
        .invoke("SendMessage", message)
        .catch((err) => console.error("Error sending message:", err));
      setInputMessage(""); // Xóa nội dung tin nhắn sau khi gửi
      setError("");
    } else {
      setError("SignalR connection is not established.");
    }
  };

  // Xử lý khi xóa tin nhắn
  const handleDeleteMessage = (messageId, conversationId) => {
    if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
      // Kiểm tra kết nối SignalR
      // Gọi hàm DeleteMessage trên server qua SignalR
      connectionRef.current
        .invoke("DeleteMessage", messageId, conversationId)
        .catch((err) => console.error("Error deleting message: ", err));
    } else {
      setError("SignalR connection is not established.");
    }
  };

  // Hàm bắt đầu kết nối SignalR
  const startSignalRConnection = async (connection) => {
    try {
      console.log("Connecting to SignalR...");
      await connection.start();
      console.log("SignalR connected successfully!");
    } catch (error) {
      console.error("SignalR connection error:", error);
      // Thử lại kết nối sau 3 giây nếu thất bại
      setTimeout(() => {
        console.log("Retrying SignalR connection...");
        startSignalRConnection(connection);
      }, 3000);
    } finally {
      setLoadingUsers(false); // Kết thúc tải danh sách người dùng
    }
  };

  // Cuộn xuống tin nhắn mới nhất khi có tin nhắn mới
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Cập nhật ID tin nhắn đã đọc cuối cùng
  useEffect(() => {
    if (messages.length > 0) {
      const lastRead = messages
        .filter(
          (message) =>
            message.isRead && message.sender.id === userLogin?.account.id
        )
        .pop();
      setLastReadMessageId(lastRead?.id || null);
    }
  }, [messages]);

  // Setup kết nối SignalR và lắng nghe sự kiện
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      const lastRead = messages
        .filter(
          (message) =>
            message.isRead && message.sender.id === userLogin?.account.id
        )
        .pop();
      setLastReadMessageId(lastRead?.id || null);
    }
  }, [messages]);

  useEffect(() => {
    const authUser = getUserLogin();
    if (!authUser) {
      navigate("/login");
      return;
    }
    setUserLogin(authUser);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://plum-village.azurewebsites.net/conversation", {
        headers: {
          Authorization: `Bearer ${authUser.accessToken}`,
        },
        transport: signalR.HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    connectionRef.current = connection;

    // Đăng ký sự kiện nhận danh sách người dùng
    connection.on("ReceiveUserList", (users) => {
      setListUsers(users.filter((user) => user.id !== authUser.account.id));
    });

    // Đăng ký sự kiện nhận lịch sử trò chuyện
    connection.on("ReceiveChatHistory", (data) => {
      const currentConversation = conversationRef.current;

      if (currentConversation === null || currentConversation.id === data.id) {
        conversationRef.current = data;
        setConversation(data);
        setMessages(data.messages);
        setLoadingConversation(false);
      }
    });

    // Đăng ký sự kiện nhận tin nhắn mới
    connection.on("ReceiveMessage", (data) => {
      const currentConversation = conversationRef.current;

      if (data.id === currentConversation?.id) {
        setConversation(data);
        setMessages(data.messages);
      } else {
        const sender = data.messages[data.messages.length - 1]?.sender;
        notificationApi(
          "success",
          "New message",
          `New message from ${sender.firstName} ${sender.lastName}`
        );
        if (sender.id && !newMessageNoti.includes(sender.id)) {
          setNewMessageNoti((prevNoti) => [...prevNoti, sender.id]);
        }
      }
    });

    // Đăng ký sự kiện nhận thông báo tin nhắn chưa đọc
    connection.on("UnReads", (senderIds) => {
      setNewMessageNoti((prevNoti) => {
        const newNoti = senderIds.filter(
          (senderId) => !prevNoti.includes(senderId)
        );
        return [...prevNoti, ...newNoti];
      });
    });

    // Bắt đầu kết nối SignalR
    startSignalRConnection(connection);

    return () => {
      connectionRef.current
        ?.stop()
        .catch((error) => console.error("Error stopping connection: ", error));
      connectionRef.current = null;
    };
  }, [navigate]);

  useEffect(() => {
    if (searchKeyword.trim() === "") {
      setFilteredUsers(listUsers);
    } else {
      const keyword = searchKeyword.toLowerCase();
      setFilteredUsers(
        listUsers.filter((user) =>
          `${user.firstName} ${user.lastName}`.toLowerCase().includes(keyword)
        )
      );
    }
  }, [searchKeyword, listUsers]);

  useEffect(() => {
    let filteredByRole = [];
    if (selectedRole === "Practitioner") {
      filteredByRole = listUsers.filter((user) => user.role === "Practitioner");
    } else if (selectedRole === "MonkNun") {
      filteredByRole = listUsers.filter((user) =>
        ["Monk", "Nun"].includes(user.role)
      );
    }

    if (searchKeyword.trim() === "") {
      setFilteredUsers(filteredByRole);
    } else {
      const keyword = searchKeyword.toLowerCase();
      setFilteredUsers(
        filteredByRole.filter((user) =>
          `${user.firstName} ${user.lastName}`.toLowerCase().includes(keyword)
        )
      );
    }
  }, [searchKeyword, listUsers, selectedRole]);

  return (
    <div className="flex h-[95vh] bg-gray-50 text-gray-900">
      <div className="w-1/4 border-r bg-white shadow-md">
        <div className="flex items-center justify-between mb-4 p-2">
          <img
            src={userLogin?.account.avatar || iconUser}
            alt={userLogin?.account.lastName}
            className="w-12 h-12 rounded-full border-2 border-purple-500"
          />
          <div className="flex flex-col ml-2 p-2">
            <p className="text-sm font-semibold truncate">
              {userLogin?.account.firstName} {userLogin?.account.lastName}
            </p>
          </div>
        </div>

        <hr className="mb-4 border-t border-gray-300" />

        <div className="mb-4">
          {/* Role Tabs */}
          <div className="flex justify-between bg-gray-50 rounded-full border border-gray-200 p-1 shadow-sm">
            <button
              className={`flex-1 py-2 text-center rounded-full text-sm transition-all duration-200 ${
                selectedRole === "Practitioner"
                  ? "bg-purple-500 text-white font-medium"
                  : "bg-transparent text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedRole("Practitioner")}
            >
              Practitioner
            </button>
            <button
              className={`flex-1 py-2 text-center rounded-full text-sm transition-all duration-200 ${
                selectedRole === "MonkNun"
                  ? "bg-purple-500 text-white font-medium"
                  : "bg-transparent text-gray-600 hover:bg-gray-200"
              }`}
              onClick={() => setSelectedRole("MonkNun")}
            >
              Monk & Nun
            </button>
          </div>
        </div>

        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by name..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              className="w-full py-2 pl-4 pr-10 border border-gray-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <HiOutlineSearch className="absolute top-2.5 right-3 w-5 h-5 text-gray-400" />
          </div>
        </div>

        <hr className="mb-4 border-t border-gray-300" />

        {/* User List */}
        <div className="overflow-y-auto overflow-x-hidden  h-[calc(95%-200px)] space-y-3">
          {isLoadingUsers ? (
            <ComLoading />
          ) : (
            [...filteredUsers]
              .sort((a, b) => {
                // Sắp xếp người dùng có tin nhắn chưa đọc lên đầu
                const aHasUnread = newMessageNoti.includes(a.id) ? 1 : 0;
                const bHasUnread = newMessageNoti.includes(b.id) ? 1 : 0;
                return bHasUnread - aHasUnread; // Người có tin nhắn chưa đọc sẽ lên đầu
              })
              .map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    newMessageNoti.includes(user.id)
                      ? "border border-blue-500 bg-blue-100"
                      : "hover:bg-gray-100"
                  }`}
                  onClick={() => {
                    handleSelectUser(user);
                    setNewMessageNoti((prevNoti) =>
                      prevNoti.filter((notiId) => notiId !== user.id)
                    );
                  }}
                >
                  <img
                    src={user.avatar || iconUser}
                    alt={user.email}
                    className="w-10 h-10 rounded-full border border-gray-300 shadow-sm"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-semibold truncate">
                      {user.firstName} {user.lastName}
                    </p>
                    <span
                      className={`text-xs ${
                        user.isOnline ? "text-green-500" : "text-gray-400"
                      }`}
                    >
                      {user.isOnline ? "Online" : "Offline"}
                    </span>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col bg-white shadow-lg rounded-l-lg">
        {selectedUser ? (
          <>
            <div className="flex items-center p-4 border-b bg-gray-100 shadow-md">
              <img
                src={selectedUser.avatar || iconUser}
                alt={selectedUser.lastName}
                className="w-12 h-12 rounded-full mr-3"
              />
              <p className="font-semibold text-lg">
                {selectedUser.firstName} {selectedUser.lastName}
              </p>
            </div>

            <div
              ref={chatContainerRef}
              className="flex-1 p-6 overflow-y-auto bg-gray-50 space-y-2"
            >
              {isLoadingConversation ? (
                <ComLoading />
              ) : (
                messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex items-start ${
                      message.sender.id === userLogin?.account.id
                        ? "justify-end"
                        : "justify-start"
                    }`}
                  >
                    {message.sender.id !== userLogin?.account.id && (
                      <div className="flex flex-col items-center mr-4">
                        <img
                          src={message.sender.avatar || iconUser}
                          alt={message.sender.lastName}
                          className="w-12 h-12 rounded-full border border-gray-300 shadow-md"
                        />
                        <span className="text-xs text-gray-500 mt-2">
                          {message.sender.firstName} {message.sender.lastName}
                        </span>
                      </div>
                    )}

                    <div className="relative max-w-xl p-1">
                      <div
                        className={`px-6 py-4 rounded-2xl shadow-md ${
                          message.sender.id === userLogin?.account.id
                            ? "bg-gradient-to-r from-blue-400 to-blue-600 text-white"
                            : "bg-white text-gray-800 border border-gray-200"
                        }`}
                      >
                        <p className="break-words text-sm leading-relaxed">
                          {message.content}
                        </p>

                        {message.sender.id === userLogin?.account.id && (
                          <MdDeleteForever
                            className="absolute top-2 right-2 text-red-500 opacity-60 hover:opacity-100 hover:scale-110 cursor-pointer transition-all duration-200"
                            onClick={() =>
                              handleDeleteMessage(message.id, conversation.id)
                            }
                          />
                        )}
                      </div>

                      <div
                        className={`text-xs mt-2 ${
                          message.sender.id === userLogin?.account.id
                            ? "text-gray-400 text-right"
                            : "text-gray-500"
                        }`}
                      >
                        {formatDate(message.sendAt)}
                      </div>

                      {message.id === lastReadMessageId && (
                        <div className="flex items-center justify-end mt-1 text-xs text-gray-400">
                          <img
                            src={selectedUser.avatar ?? iconUser}
                            alt="Avatar"
                            className="w-4 h-4 border border-purple-700 rounded-full mr-1"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 border-t bg-white flex items-center">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 p-3 border rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-all duration-200"
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
              />
              <button
                onClick={handleSendMessage}
                className="ml-2 bg-blue-500 text-white px-5 py-2 rounded-full hover:bg-blue-600 transition-all duration-200"
              >
                Send
              </button>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500 text-lg">
              Select a user to start chatting
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatApp;

// Hàm lấy thông tin người dùng từ localStorage
const getUserLogin = () => {
  const accessToken = localStorage.getItem("accessToken");
  const userString = localStorage.getItem("use");
  if (
    !accessToken ||
    accessToken.trim() === "" ||
    !userString ||
    userString.trim() === ""
  ) {
    return null;
  }
  try {
    const token =
      accessToken.startsWith('"') && accessToken.endsWith('"')
        ? JSON.parse(accessToken)
        : accessToken;
    const user = JSON.parse(userString);
    if (!user.id || typeof user !== "object") {
      return null;
    }
    return { accessToken: token, account: user };
  } catch (error) {
    console.error("Error parsing user or token data:", error);
    return null;
  }
};

// Hàm định dạng ngày tháng
const formatDate = (date) => {
  const formatDate = new Date(date);
  const day = String(formatDate.getDate()).padStart(2, "0");
  const month = String(formatDate.getMonth() + 1).padStart(2, "0");
  const year = formatDate.getFullYear();
  const hours = String(formatDate.getHours()).padStart(2, "0");
  const minutes = String(formatDate.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes} | ${day}-${month}-${year}`;
};
